import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    CheckBox,
    DateInput,
    Form,
    FormField,
    Heading,
    List,
    Page,
    PageContent,
    Select,
    TextArea,
    TextInput,
    Tip,
} from 'grommet';
import {
    API,
    JobService,
    FilterOptionSelect,
    Utils,
    SearchSelect,
    ApiSearch,
    GRAD_YEAR_OPTIONS,
    DEGREE_TYPES,
    MatchWeightFields,
} from '@enrich/shared-lib';
import { useLoaderData, useNavigate } from 'react-router-dom';
import QueryString from 'qs';

export const EditJobForm = () => {
    const navigate = useNavigate();
    const job = Utils.deepFlatten(useLoaderData());
    const [companyPipelines, setCompanyPipelines] = useState([]);
    const [value, setValue] = useState({
        ...job,
        autoMatchEnabled: !!job.autoMatchEnabled,
        locations: job.job_locations
            ?.filter((j) => j.location)
            .map((j) => j.location),
        resumeReadyWeight: job.resumeReadyWeight ?? 0,
        welcomeCallCompletedWeight: job.welcomeCallCompletedWeight ?? 0,
    });
    const [buttonBusy, setButtonBusy] = useState(false);

    useEffect(() => {
        (async () => {
            const loadedCompanyPipelines = await getCompanyPipelines(
                job.company.id,
            );

            setCompanyPipelines(Utils.deepFlatten(loadedCompanyPipelines));
        })();
    }, []);

    const handleSubmit = async (jobData) => {
        setButtonBusy(true);
        await new JobService().updateJob(job.id, jobData);
        navigate(`/jobs/${job.id}`);
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent align="center">
                <Box
                    elevation="large"
                    pad={{
                        top: 'medium',
                        bottom: 'xlarge',
                        left: 'xlarge',
                        right: 'xlarge',
                    }}>
                    <Heading level="2">Editing: {job.title}</Heading>
                    <Box align="center">
                        <Form
                            value={value}
                            onChange={(nextValue) => setValue(nextValue)}
                            onReset={() => setValue({})}
                            onSubmit={({ value }) => {
                                handleSubmit(value);
                            }}>
                            <Box gap="medium" width="large">
                                <FormField
                                    label="Job Title"
                                    name="title"
                                    required>
                                    <TextInput name="title" />
                                </FormField>

                                <Box
                                    direction="row-responsive"
                                    fill="horizontal"
                                    justify="between"
                                    gap={'large'}>
                                    <Box basis="large">
                                        <FormField
                                            label="Application Link"
                                            name="applicationLink">
                                            <TextInput name="applicationLink" />
                                        </FormField>
                                    </Box>
                                    <Box basis="medium">
                                        <FormField label="Status" name="status">
                                            <Tip content='Note: changing the status to "closed" also closes all job matches that are at the "assigned" or "interested" stages.'>
                                                <Select
                                                    name="status"
                                                    options={[
                                                        {
                                                            value: 'demo',
                                                            label: 'Demo',
                                                        },
                                                        {
                                                            value: 'preparing',
                                                            label: 'Future Job',
                                                        },
                                                        {
                                                            value: 'open',
                                                            label: 'Open',
                                                        },
                                                        {
                                                            value: 'closed',
                                                            label: 'Closed',
                                                        },
                                                    ]}
                                                    labelKey="label"
                                                    valueKey={{
                                                        key: 'value',
                                                        reduce: true,
                                                    }}
                                                    margin={{
                                                        vertical: 'small',
                                                    }}
                                                />
                                            </Tip>
                                        </FormField>
                                    </Box>
                                    <Box basis="medium">
                                        <FormField label="Seats" name="seats">
                                            <TextInput
                                                type={'number'}
                                                name="seats"
                                                margin={{
                                                    vertical: 'small',
                                                }}
                                            />
                                        </FormField>
                                    </Box>
                                </Box>

                                <Box
                                    direction="row-responsive"
                                    fill="horizontal"
                                    gap={'large'}>
                                    <Box fill="horizontal">
                                        <FormField
                                            label="Pipeline"
                                            name="pipeline"
                                            htmlFor="pipeline">
                                            <SearchSelect
                                                name="pipeline"
                                                labelKey="name"
                                                valueKey="id"
                                                defaultValue={job.pipeline?.id}
                                                options={companyPipelines}
                                                updateFormData={(val) =>
                                                    setValue({
                                                        ...value,
                                                        pipeline: val.id,
                                                    })
                                                }
                                            />
                                        </FormField>
                                    </Box>
                                </Box>

                                <Box
                                    direction="row-responsive"
                                    fill="horizontal"
                                    gap={'large'}>
                                    <Box basis="medium">
                                        <CheckBox
                                            name="isReportingActive"
                                            label="Reporting Status"
                                            toggle
                                            checked={!!value.isReportingActive}
                                            onChange={(event) =>
                                                setValue({
                                                    ...value,
                                                    isReportingActive:
                                                        event.target.checked,
                                                })
                                            }
                                        />
                                    </Box>
                                    <Box basis="medium">
                                        <CheckBox
                                            name="autoMatchEnabled"
                                            label="Toggle Auto-Match"
                                            toggle
                                            checked={!!value.autoMatchEnabled}
                                            onChange={(event) =>
                                                setValue({
                                                    ...value,
                                                    autoMatchEnabled:
                                                        event.target.checked,
                                                })
                                            }
                                        />
                                    </Box>
                                </Box>

                                <FormField label="Location" name="location">
                                    <TextInput name="location" />
                                </FormField>

                                <FormField label="Location(s)" name="locations">
                                    <ApiSearch
                                        contentType="locations"
                                        selectMultiple={true}
                                        name="locations"
                                        defaultValue={job.job_locations
                                            ?.map((j) => j.location)
                                            .filter(Boolean)}
                                        updateFormData={(val) => {
                                            setValue({
                                                ...value,
                                                locations: val,
                                            });
                                        }}
                                    />
                                </FormField>

                                <Card
                                    fill="horizontal"
                                    width="medium"
                                    height={{ min: 'xsmall', max: 'small' }}
                                    background="white"
                                    elevation="medium"
                                    round="small">
                                    <CardHeader
                                        pad={{
                                            horizontal: 'medium',
                                            vertical: 'small',
                                        }}
                                        background="dark-5"
                                        round={{
                                            corner: 'top',
                                            size: 'small',
                                        }}>
                                        <Heading
                                            level="4"
                                            margin="none"
                                            color="white">
                                            ATS Job Locations
                                        </Heading>
                                    </CardHeader>
                                    <CardBody
                                        pad="none"
                                        overflow={{ vertical: 'auto' }}>
                                        <List
                                            data={job.job_locations
                                                .filter((j) => j.mergeId)
                                                .map((j) => ({
                                                    id: j.id,
                                                    name: `${j.officeName} ${j.mergeLocation}`,
                                                }))}
                                            pad={{
                                                horizontal: 'medium',
                                                vertical: 'small',
                                            }}
                                            primaryKey={'name'}
                                        />
                                    </CardBody>
                                </Card>

                                <FormField
                                    label="Preview Description"
                                    name="cardDescription">
                                    <TextInput name="cardDescription" />
                                </FormField>

                                <FormField
                                    label="Responsibilities"
                                    name="responsibilities">
                                    <TextArea name="responsibilities" />
                                </FormField>

                                <FormField
                                    label="Interview Process"
                                    name="interviewProcess">
                                    <TextArea name="interviewProcess" />
                                </FormField>

                                <FormField
                                    label="Salary & Benefits"
                                    name="salaryAndBenefits">
                                    <TextArea name="salaryAndBenefits" />
                                </FormField>

                                <FormField
                                    label="Additional Details"
                                    name="additionalDetails">
                                    <TextArea name="additionalDetails" />
                                </FormField>

                                <FormField
                                    label="Eligible Graduation Years"
                                    name="eligibleGradYears">
                                    <FilterOptionSelect
                                        placeholder="Graduation Years"
                                        options={GRAD_YEAR_OPTIONS}
                                        defaultValue={job.eligibleGradYears}
                                        updateFormData={(val) => {
                                            setValue({
                                                ...value,
                                                eligibleGradYears: val,
                                            });
                                        }}
                                    />
                                </FormField>

                                <FormField
                                    label="Eligible Degree Types"
                                    name="eligibleDegreeTypes">
                                    <FilterOptionSelect
                                        placeholder="Degree Types"
                                        options={DEGREE_TYPES}
                                        defaultValue={job.eligibleDegreeTypes}
                                        updateFormData={(val) => {
                                            setValue({
                                                ...value,
                                                eligibleDegreeTypes: val,
                                            });
                                        }}
                                    />
                                </FormField>

                                <FormField
                                    label="Minimum GPA Required"
                                    name="minimumGPA">
                                    <TextInput
                                        type="number"
                                        name="minimumGPA"
                                    />
                                </FormField>

                                <FormField
                                    label="Ideal Majors"
                                    name="idealMajors">
                                    <ApiSearch
                                        contentType="majors"
                                        selectMultiple={true}
                                        name="idealMajors"
                                        defaultValue={job.idealMajors}
                                        updateFormData={(val) => {
                                            setValue({
                                                ...value,
                                                idealMajors: val,
                                            });
                                        }}
                                    />
                                </FormField>

                                <FormField
                                    label="Technical Skills"
                                    name="relevantTechnicalSkills">
                                    <ApiSearch
                                        contentType="technical-skills"
                                        selectMultiple={true}
                                        name="relevantTechnicalSkills"
                                        defaultValue={
                                            job.relevantTechnicalSkills
                                        }
                                        updateFormData={(val) => {
                                            setValue({
                                                ...value,
                                                relevantTechnicalSkills: val,
                                            });
                                        }}
                                    />
                                </FormField>

                                <FormField label="Industries" name="industries">
                                    <ApiSearch
                                        contentType="industries"
                                        selectMultiple={true}
                                        name="industries"
                                        defaultValue={job.industries}
                                        updateFormData={(val) => {
                                            setValue({
                                                ...value,
                                                industries: val,
                                            });
                                        }}
                                    />
                                </FormField>

                                <FormField
                                    label="Job Functions"
                                    name="jobFunctions">
                                    <ApiSearch
                                        contentType="job-functions"
                                        selectMultiple={true}
                                        name="jobFunctions"
                                        defaultValue={job.jobFunctions}
                                        updateFormData={(val) => {
                                            setValue({
                                                ...value,
                                                jobFunctions: val,
                                            });
                                        }}
                                    />
                                </FormField>

                                <FormField
                                    label="Application Deadline"
                                    name="applicationDeadline"
                                    width="small"
                                    info="only set if there is a hard application deadline">
                                    <DateInput
                                        name="applicationDeadline"
                                        format="mm/dd/yyyy"
                                        onChange={(e) => {
                                            setValue({
                                                ...value,
                                                applicationDeadline: e.value,
                                            });
                                        }}
                                        buttonProps={{
                                            color: 'black',
                                            margin: 'small',
                                        }}
                                        calendarProps={{
                                            daysOfWeek: true,
                                            size: 'medium',
                                        }}
                                        dropProps={{
                                            background: 'enrich-grey',
                                            color: 'enrich-green',
                                            pad: 'small',
                                        }}
                                    />
                                </FormField>

                                <Box direction="row" gap="small">
                                    <FormField
                                        label="Days to show interest"
                                        name="daysToShowInterest">
                                        <TextInput name="daysToShowInterest" />
                                    </FormField>
                                    <FormField
                                        label="Days to apply"
                                        name="daysToApply">
                                        <TextInput name="daysToApply" />
                                    </FormField>
                                </Box>

                                <FormField name="coverLetterRequired">
                                    <CheckBox
                                        name="coverLetterRequired"
                                        checked={value.coverLetterRequired}
                                        label="Cover letter required"
                                        onChange={(event) =>
                                            setValue({
                                                ...value,
                                                coverLetterRequired:
                                                    event.target.checked,
                                            })
                                        }
                                    />
                                </FormField>

                                <FormField name="ignoreInterestDeadline">
                                    <CheckBox
                                        name="ignoreInterestDeadline"
                                        checked={value.ignoreInterestDeadline}
                                        label="Ignore interest deadline"
                                        onChange={(event) =>
                                            setValue({
                                                ...value,
                                                ignoreInterestDeadline:
                                                    event.target.checked,
                                            })
                                        }
                                    />
                                </FormField>

                                <FormField
                                    name="isRemote"
                                    style={{
                                        border: { color: 'transparent' },
                                    }}>
                                    <CheckBox
                                        name="isRemote"
                                        checked={value.isRemote}
                                        label="This is a remote position"
                                        onChange={(event) =>
                                            setValue({
                                                ...value,
                                                isRemote: event.target.checked,
                                            })
                                        }
                                    />
                                </FormField>

                                <FormField
                                    name="wouldSponsorVisa"
                                    style={{
                                        border: { color: 'transparent' },
                                    }}>
                                    <CheckBox
                                        name="wouldSponsorVisa"
                                        checked={value.wouldSponsorVisa}
                                        label="Would sponsor a visa"
                                        onChange={(event) =>
                                            setValue({
                                                ...value,
                                                wouldSponsorVisa:
                                                    event.target.checked,
                                            })
                                        }
                                    />
                                </FormField>

                                <MatchWeightFields
                                    value={value}
                                    setValue={setValue}
                                />

                                <Box
                                    align="center"
                                    justify="center"
                                    direction="row-responsive"
                                    gap="medium">
                                    <Button
                                        secondary
                                        label="Back to Job Summary"
                                        size="large"
                                        onClick={() =>
                                            navigate(`/jobs/${job.id}`)
                                        }
                                    />
                                    <Button
                                        type="submit"
                                        primary
                                        busy={buttonBusy}
                                        label="Save Job Details"
                                        size="large"
                                    />
                                </Box>
                            </Box>
                        </Form>
                    </Box>
                </Box>
            </PageContent>
        </Page>
    );
};

export const loader = async ({ params }) => {
    const query = QueryString.stringify({
        populate: {
            job_locations: {
                populate: ['location'],
            },
            pipeline: { fields: ['id', 'name'] },
            company: { fields: ['id', 'name'] },
            idealMajors: { fields: ['id', 'name'] },
            industries: { fields: ['id', 'name'] },
            jobFunctions: { fields: ['id', 'name'] },
            relevantTechnicalSkills: { fields: ['id', 'name'] },
        },
    });

    try {
        const response = await API.get(`/api/jobs/${params.jobId}?${query}`);
        return response.data.data;
    } catch (error) {
        return null;
    }
};

const getCompanyPipelines = async (companyId) => {
    const query = QueryString.stringify({
        fields: ['id', 'name'],
        filters: { company: { id: companyId } },
        pagination: {
            limit: 500,
        },
        sort: ['name:asc'],
    });

    try {
        const response = await API.get(`/api/pipelines?${query}`);
        return response.data.data;
    } catch (error) {
        console.log(error);
    }

    return [];
};
